import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { uploadFile } from '../utils/fileUpload';
import {
  CreditCard,
  Plus,
  Trash2,
  Search,
  Filter,
  Download,
  X,
  Camera,
  Receipt,
  Building2,
  User,
  Calendar,
  DollarSign,
  FileText,
  Eye,
  ChevronDown,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Clock,
  Hash,
  MapPin
} from 'lucide-react';

interface Worker {
  id: string;
  name: string;
}

interface Site {
  id: string;
  name: string;
  address: string;
}

interface ArchivedSite {
  original_site_id: string;
  name: string;
}

interface CreditCardExpense {
  id: string;
  employee_name: string;
  worker_id: string;
  merchant: string;
  amount: number;
  business_purpose: string;
  site_id: string | null;
  site_label: string | null;
  expense_date: string;
  receipt_photo_url: string | null;
  created_at: string;
}

interface ExpenseForm {
  worker_id: string;
  employee_name: string;
  merchant: string;
  amount: string;
  business_purpose: string;
  site_id: string;
  expense_date: string;
}

const emptyForm: ExpenseForm = {
  worker_id: '',
  employee_name: '',
  merchant: '',
  amount: '',
  business_purpose: '',
  site_id: '',
  expense_date: new Date().toISOString().split('T')[0],
};

export const CreditCardExpensesPage: React.FC = () => {
  const [expenses, setExpenses] = useState<CreditCardExpense[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [archivedSites, setArchivedSites] = useState<ArchivedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<ExpenseForm>(emptyForm);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState<string | null>(null);
  const [viewingExpense, setViewingExpense] = useState<CreditCardExpense | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWorker, setFilterWorker] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchExpenses(), fetchWorkers(), fetchSites(), fetchArchivedSites()]);
    setLoading(false);
  };

  const fetchExpenses = async () => {
    const { data, error } = await supabase
      .from('credit_card_expenses')
      .select('*')
      .order('expense_date', { ascending: false });
    if (!error && data) setExpenses(data);
  };

  const fetchWorkers = async () => {
    const { data } = await supabase
      .from('workers')
      .select('id, name')
      .eq('status', 'active')
      .order('name');
    if (data) setWorkers(data);
  };

  const fetchSites = async () => {
    const { data } = await supabase
      .from('sites')
      .select('id, name, address')
      .order('name');
    if (data) setSites(data);
  };

  const fetchArchivedSites = async () => {
    const { data } = await supabase
      .from('archived_sites')
      .select('original_site_id, site_data')
      .order('archived_at', { ascending: false });
    if (data) {
      const parsed: ArchivedSite[] = data
        .map((row: { original_site_id: string; site_data: { name?: string } | null }) => ({
          original_site_id: row.original_site_id,
          name: (row.site_data?.name ?? '').trim(),
      }))
      .filter((s: ArchivedSite) => s.name);
      setArchivedSites(parsed);
    }
  };

  const archivedSiteNames = React.useMemo(
    () => new Set(archivedSites.map(s => s.name.toLowerCase())),
    [archivedSites]
  );

  const isArchivedExpense = (expense: CreditCardExpense) =>
    !expense.site_id && !!expense.site_label && archivedSiteNames.has(expense.site_label.toLowerCase());

  const handleWorkerChange = (workerId: string) => {
    const worker = workers.find(w => w.id === workerId);
    setForm(prev => ({
      ...prev,
      worker_id: workerId,
      employee_name: worker?.name || '',
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceiptFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setReceiptPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.worker_id || !form.merchant || !form.amount || !form.business_purpose || !form.expense_date) {
      setError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    setError(null);

    let receipt_photo_url: string | null = null;
    if (receiptFile) {
      const result = await uploadFile(receiptFile, 'receipts', 'credit-card-expenses');
      if (!result.success) {
        setError(result.error || 'Failed to upload receipt.');
        setSubmitting(false);
        return;
      }
      receipt_photo_url = result.url || null;
    }

    const selectedSite = sites.find(s => s.id === form.site_id);
    const { error: insertError } = await supabase.from('credit_card_expenses').insert([{
      worker_id: form.worker_id,
      employee_name: form.employee_name,
      merchant: form.merchant,
      amount: parseFloat(form.amount),
      business_purpose: form.business_purpose,
      site_id: form.site_id || null,
      site_label: selectedSite ? selectedSite.name : null,
      expense_date: new Date(form.expense_date).toISOString(),
      receipt_photo_url,
    }]);

    if (insertError) {
      setError('Failed to save expense: ' + insertError.message);
    } else {
      setSuccess('Expense recorded successfully.');
      setShowModal(false);
      resetForm();
      await fetchExpenses();
      setTimeout(() => setSuccess(null), 3000);
    }
    setSubmitting(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('credit_card_expenses').delete().eq('id', id);
    if (!error) {
      setExpenses(prev => prev.filter(e => e.id !== id));
    }
    setDeleteConfirm(null);
  };

  const resetForm = () => {
    setForm(emptyForm);
    setReceiptFile(null);
    setReceiptPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openModal = () => {
    resetForm();
    setError(null);
    setShowModal(true);
  };

  const filtered = expenses.filter(e => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      e.employee_name.toLowerCase().includes(q) ||
      e.merchant.toLowerCase().includes(q) ||
      e.business_purpose.toLowerCase().includes(q) ||
      (e.site_label || '').toLowerCase().includes(q);
    const matchesWorker = !filterWorker || e.worker_id === filterWorker;
    const matchesSite = !filterSite ||
      e.site_id === filterSite ||
      (!e.site_id && e.site_label?.toLowerCase() === filterSite.toLowerCase());
    const expenseDay = e.expense_date.split('T')[0];
    const matchesFrom = !filterDateFrom || expenseDay >= filterDateFrom;
    const matchesTo = !filterDateTo || expenseDay <= filterDateTo;
    return matchesSearch && matchesWorker && matchesSite && matchesFrom && matchesTo;
  });

  const totalAmount = filtered.reduce((sum, e) => sum + Number(e.amount), 0);

  // Group by purpose for summary
  const byPurpose: Record<string, number> = {};
  filtered.forEach(e => {
    byPurpose[e.business_purpose] = (byPurpose[e.business_purpose] || 0) + Number(e.amount);
  });
  const topPurposes = Object.entries(byPurpose)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-7 w-7 text-orange-500" />
            Credit Card Expenditure
          </h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage employee credit card expenses</p>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Expense
        </button>
      </div>

      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total (filtered)</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Receipt className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Transactions</p>
              <p className="text-xl font-bold text-gray-900">{filtered.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Avg. per transaction</p>
              <p className="text-xl font-bold text-gray-900">
                {filtered.length > 0 ? formatCurrency(totalAmount / filtered.length) : '$0.00'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Categories */}
      {topPurposes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Spend by Purpose</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {topPurposes.map(([purpose, amount]) => (
              <div key={purpose} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 truncate">{purpose}</p>
                <p className="text-sm font-bold text-gray-800 mt-0.5">{formatCurrency(amount)}</p>
                <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-400 rounded-full"
                    style={{ width: `${Math.min(100, (amount / totalAmount) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by employee, merchant, purpose or site..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(f => !f)}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
              showFilters ? 'bg-orange-50 border-orange-300 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
            <ChevronDown className={`h-3 w-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-1">
            <select
              value={filterWorker}
              onChange={e => setFilterWorker(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Employees</option>
              {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <select
              value={filterSite}
              onChange={e => setFilterSite(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Sites</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              {archivedSites.length > 0 && (
                <option value="" disabled>── Archived ──</option>
              )}
              {archivedSites.map(s => (
                <option key={s.original_site_id} value={s.name}>{s.name} (Archived)</option>
              ))}
            </select>
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Receipt className="h-12 w-12 mb-3 opacity-30" />
            <p className="font-medium">No expenses found</p>
            <p className="text-sm mt-1">Add a new expense to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Merchant</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Purpose</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Site</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Receipt</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(expense => (
                  <tr
                    key={expense.id}
                    onClick={() => setViewingExpense(expense)}
                    className="hover:bg-orange-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(expense.expense_date)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-3.5 w-3.5 text-orange-600" />
                        </div>
                        <span className="font-medium text-gray-800">{expense.employee_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{expense.merchant}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {expense.business_purpose}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {expense.site_label ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-600">{expense.site_label}</span>
                          {isArchivedExpense(expense) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600">
                              Archived
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                      {formatCurrency(Number(expense.amount))}
                    </td>
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      {expense.receipt_photo_url ? (
                        <button
                          onClick={() => setViewingReceipt(expense.receipt_photo_url)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View receipt"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      {deleteConfirm === expense.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleDelete(expense.id)}
                            className="text-xs bg-red-500 text-white px-2 py-0.5 rounded hover:bg-red-600"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(expense.id)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-sm font-semibold text-gray-700">
                    Total ({filtered.length} {filtered.length === 1 ? 'transaction' : 'transactions'})
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">{formatCurrency(totalAmount)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Add Credit Card Expense</h2>
              <button onClick={() => { setShowModal(false); resetForm(); setError(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Employee */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="inline h-4 w-4 mr-1" />
                  Employee <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.worker_id}
                  onChange={e => handleWorkerChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select employee...</option>
                  {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>

              {/* Expense Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Expense Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.expense_date}
                  onChange={e => setForm(prev => ({ ...prev, expense_date: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Merchant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="inline h-4 w-4 mr-1" />
                  Merchant <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.merchant}
                  onChange={e => setForm(prev => ({ ...prev, merchant: e.target.value }))}
                  placeholder="e.g. Bunnings, Coles, Total Tools"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DollarSign className="inline h-4 w-4 mr-1" />
                  Amount (AUD) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amount}
                    onChange={e => setForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    required
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* Business Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Business Purpose <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.business_purpose}
                  onChange={e => setForm(prev => ({ ...prev, business_purpose: e.target.value }))}
                  placeholder="e.g. Site materials, Safety equipment, Fuel"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {/* Site */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="inline h-4 w-4 mr-1" />
                  Associated Site <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <select
                  value={form.site_id}
                  onChange={e => setForm(prev => ({ ...prev, site_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">No site</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Receipt Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Camera className="inline h-4 w-4 mr-1" />
                  Receipt Photo <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {receiptPreview ? (
                  <div className="relative">
                    <img
                      src={receiptPreview}
                      alt="Receipt preview"
                      className="w-full max-h-40 object-contain border border-gray-200 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => { setReceiptFile(null); setReceiptPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow text-gray-500 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-500 hover:border-orange-400 hover:text-orange-500 transition-colors"
                  >
                    <Camera className="h-5 w-5 mx-auto mb-1" />
                    Click to upload receipt
                  </button>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); setError(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Detail Modal */}
      {viewingExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Receipt className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Expense Detail</h2>
                  <p className="text-xs text-gray-500">{viewingExpense.merchant}</p>
                </div>
              </div>
              <button onClick={() => setViewingExpense(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Amount — hero */}
              <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-center">
                <p className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">Amount</p>
                <p className="text-3xl font-bold text-orange-700">{formatCurrency(Number(viewingExpense.amount))}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <User className="h-3 w-3" /> Employee
                  </div>
                  <p className="text-sm font-semibold text-gray-800">{viewingExpense.employee_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <Building2 className="h-3 w-3" /> Merchant
                  </div>
                  <p className="text-sm font-semibold text-gray-800">{viewingExpense.merchant}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <FileText className="h-3 w-3" /> Purpose
                  </div>
                  <p className="text-sm font-semibold text-gray-800">{viewingExpense.business_purpose}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1">
                    <MapPin className="h-3 w-3" /> Site
                  </div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-gray-800">{viewingExpense.site_label || 'No site'}</p>
                    {isArchivedExpense(viewingExpense) && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-200 text-gray-600">
                        Archived
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="border border-gray-100 rounded-lg divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar className="h-3.5 w-3.5" /> Expense Date
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    {new Date(viewingExpense.expense_date).toLocaleString('en-AU', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                      hour12: true
                    })}
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Clock className="h-3.5 w-3.5" /> Record Created
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    {new Date(viewingExpense.created_at).toLocaleString('en-AU', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                      hour12: true
                    })}
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Hash className="h-3.5 w-3.5" /> Record ID
                  </div>
                  <p className="text-xs font-mono text-gray-500 truncate max-w-[180px]" title={viewingExpense.id}>
                    {viewingExpense.id}
                  </p>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Hash className="h-3.5 w-3.5" /> Worker ID
                  </div>
                  <p className="text-xs font-mono text-gray-500 truncate max-w-[180px]" title={viewingExpense.worker_id}>
                    {viewingExpense.worker_id}
                  </p>
                </div>
              </div>

              {viewingExpense.receipt_photo_url && (
                <div>
                  <p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5">
                    <Camera className="h-3.5 w-3.5" /> Receipt
                  </p>
                  <img
                    src={viewingExpense.receipt_photo_url}
                    alt="Receipt"
                    className="w-full max-h-48 object-contain border border-gray-200 rounded-lg cursor-pointer hover:opacity-90"
                    onClick={() => { setViewingExpense(null); setViewingReceipt(viewingExpense.receipt_photo_url); }}
                  />
                  <p className="text-xs text-gray-400 mt-1 text-center">Click image to view full size</p>
                </div>
              )}
            </div>

            <div className="px-5 pb-5">
              <button
                onClick={() => setViewingExpense(null)}
                className="w-full py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Viewer Modal */}
      {viewingReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-2xl w-full">
            <button
              onClick={() => setViewingReceipt(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
            <img
              src={viewingReceipt}
              alt="Receipt"
              className="w-full rounded-lg shadow-2xl object-contain max-h-[80vh]"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditCardExpensesPage;
